import { z } from 'zod';

import { PAYMENT_DEFAULTS, SETTING_KEYS } from '../constants/pricing';
import { SQUAD_DEFAULTS } from '../constants/squad';
import { SeasonStatusSchema, TournamentFormatSchema } from '../enums';
import { HHmmSchema, IdSchema, IsoDateSchema, KopecksSchema } from './common';
import { ActivityRefSchema, GatheringPublicSchema } from './gathering';
import { SeasonPricePreviewSchema } from './order';

/**
 * Сезоны и сборы (Stage 5): публичный DTO `season.current`/`season.byId`,
 * шаблоны сезона, входные схемы конструктора и настроек админки.
 */

// ───────────────────────────── шаблоны ─────────────────────────────

/** Слот шаблона: `sport` (основная активность отряда) или код неосновной активности. */
export const SeasonSlotSchema = z
  .string()
  .regex(/^(sport|[a-z0-9][a-z0-9-]{1,39})$/, 'Слот: sport или код активности');

export const SeasonSlotsSchema = z
  .array(SeasonSlotSchema)
  .length(SQUAD_DEFAULTS.gatheringsPerSeason, `В сезоне ${SQUAD_DEFAULTS.gatheringsPerSeason} сборов`);

export const SeasonalSchema = z.enum(['winter', 'summer']);

export const SeasonTemplatePublicSchema = z.object({
  id: IdSchema,
  code: z.string(),
  name: z.string(),
  slots: SeasonSlotsSchema,
  seasonal: SeasonalSchema.nullable(),
});
export type SeasonTemplatePublic = z.infer<typeof SeasonTemplatePublicSchema>;

// ───────────────────────────── публичный DTO ─────────────────────────────

/**
 * Состояние оплаты сезона участником по его `Order` (Stage 5; Stage 7 добавил REFUNDED —
 * деньги вернулись после провала дедлайна или отмены).
 */
export const SeasonPaymentStateSchema = z.enum(['NONE', 'PENDING', 'PAID', 'REFUNDED']);
export type SeasonPaymentState = z.infer<typeof SeasonPaymentStateSchema>;

/**
 * Продление сезона (Stage 12, задача 5): черновик следующего сезона со скидкой
 * `pricing.renewalDiscount` до дедлайна. Показывается на `/season` кнопкой «Продлить».
 */
export const SeasonRenewalSchema = z.object({
  nextSeasonId: IdSchema,
  number: z.number().int().min(1),
  /** Цена со скидкой продления — столько платит участник */
  priceKopecks: KopecksSchema,
  /** Цена сезона без скидки */
  listPriceKopecks: KopecksSchema,
  discountPct: z.number().int().min(0).max(100),
  deadline: z.string().datetime(),
  startsOn: IsoDateSchema,
  endsOn: IsoDateSchema,
  myPayment: SeasonPaymentStateSchema,
  /** Сколько человек уже продлили */
  renewedCount: z.number().int().nonnegative(),
  minToLaunch: z.number().int().positive(),
});
export type SeasonRenewal = z.infer<typeof SeasonRenewalSchema>;

export const SeasonPublicSchema = z.object({
  id: IdSchema,
  squadId: IdSchema,
  number: z.number().int().min(1),
  status: SeasonStatusSchema,
  priceKopecks: KopecksSchema,
  startsOn: IsoDateSchema,
  endsOn: IsoDateSchema,
  saleOpensAt: z.string().datetime().nullable(),
  /** Срок оплаты и гарантии набора — только у сезона в продаже (SALE), иначе `null` (раунд 3, M2) */
  saleDeadline: z.string().datetime().nullable(),
  template: z.object({ code: z.string(), name: z.string() }).nullable(),
  /**
   * Основная активность отряда. Нужна экрану расписания: сезон намеренно смешивает её с
   * дополнительными форматами (баня, квиз, мастерская), и без пометки это выглядит ошибкой данных.
   */
  primaryActivity: ActivityRefSchema,
  /** Сборы по порядку, без отменённых при переносе (у перенесённого тот же `index`) */
  gatherings: z.array(GatheringPublicSchema),
  gatheringsDone: z.number().int().nonnegative(),
  gatheringsTotal: z.number().int().nonnegative(),
  nextGatheringId: IdSchema.nullable(),
  myPayment: SeasonPaymentStateSchema,
  /** Последний заказ сезона участника (для `/pay/result/:orderId`), Stage 7 */
  myOrderId: IdSchema.nullable(),
  /** Дедлайн гарантии уже продлевали (Stage 7) */
  deadlineExtended: z.boolean(),
  /**
   * Сколько основных участников состава уже оплатили сезон и сколько нужно для запуска.
   * Гарантия набора обещана всем на экране оплаты — без этих чисел обещание непроверяемо:
   * состав не видит, идёт ли дело к запуску или к возврату (Stage 16, раунд 1).
   */
  paidCount: z.number().int().nonnegative(),
  minToLaunch: z.number().int().positive(),
  /** Предложение продлить сезон (Stage 12); null — рано или продление уже оплачено и запущено */
  renewal: SeasonRenewalSchema.nullable(),
  /** Этот сезон сам создан продлением: скидка на нём (Stage 12, задача 5) */
  renewalDiscountPct: z.number().int().min(0).max(100).nullable(),
  /**
   * Цена сезона именно для этого участника: со скидкой капитана, продления, компании, зачётом
   * пробного сбора и реферальным бонусом. `null` — сезон не продаётся или участник не в составе.
   * Экран оплаты обязан показывать её, а не прайс-лист.
   */
  myPrice: SeasonPricePreviewSchema.nullable(),
});
export type SeasonPublic = z.infer<typeof SeasonPublicSchema>;

export const SeasonIdInputSchema = z.object({ seasonId: IdSchema });
export const GatheringIdInputSchema = z.object({ gatheringId: IdSchema });

// ───────────────────────────── конструктор (админка) ─────────────────────────────

const NotesSchema = z.string().trim().max(500).nullable();
const ReasonSchema = z.string().trim().max(500).nullable();
const DurationSchema = z.number().int().min(15).max(600);
const MinAttendanceSchema = z.number().int().min(1).max(32);

/** Черновик сбора в превью конструктора: правится до сохранения сезона. */
export const AdminGatheringDraftSchema = z.object({
  index: z.number().int().min(1).max(12),
  activityId: IdSchema,
  venueId: IdSchema.nullable(),
  date: IsoDateSchema,
  time: HHmmSchema,
  durationMin: DurationSchema,
  minAttendance: MinAttendanceSchema,
  notes: NotesSchema,
  opponentSquadId: IdSchema.nullable(),
});
export type AdminGatheringDraft = z.infer<typeof AdminGatheringDraftSchema>;

export const AdminSeasonPreviewInputSchema = z.object({
  squadId: IdSchema,
  templateCode: z.string().trim().min(1).max(40),
  startsOn: IsoDateSchema,
});
export type AdminSeasonPreviewInput = z.infer<typeof AdminSeasonPreviewInputSchema>;

export const AdminSeasonCreateInputSchema = AdminSeasonPreviewInputSchema.extend({
  /** null — цена активности или `pricing.seasonDefault` */
  priceKopecks: KopecksSchema.nullable().default(null),
  /** Пусто — сборы строго по шаблону; иначе — правленое превью (индексы 1..N без пропусков) */
  gatherings: z.array(AdminGatheringDraftSchema).max(12).default([]),
}).refine(
  (v) =>
    v.gatherings.length === 0 ||
    v.gatherings
      .map((g) => g.index)
      .sort((a, b) => a - b)
      .every((idx, i) => idx === i + 1),
  { message: 'Индексы сборов должны идти подряд с 1', path: ['gatherings'] },
);
export type AdminSeasonCreateInput = z.infer<typeof AdminSeasonCreateInputSchema>;

export const AdminSeasonsListInputSchema = z.object({
  cityId: IdSchema.optional(),
  squadId: IdSchema.optional(),
  status: z.array(SeasonStatusSchema).max(5).optional(),
});
export type AdminSeasonsListInput = z.infer<typeof AdminSeasonsListInputSchema>;

export const AdminSeasonCancelInputSchema = z.object({
  seasonId: IdSchema,
  reason: ReasonSchema.default(null),
});

/** Продление дедлайна гарантии набора (Stage 7, задача 3): ops, ограниченное число раз. */
export const AdminSeasonExtendDeadlineInputSchema = z.object({
  seasonId: IdSchema,
  days: z.number().int().min(1).max(PAYMENT_DEFAULTS.deadlineExtendMaxDays),
});
export type AdminSeasonExtendDeadlineInput = z.infer<typeof AdminSeasonExtendDeadlineInputSchema>;

export const AdminGatheringUpdateInputSchema = z.object({
  gatheringId: IdSchema,
  activityId: IdSchema.optional(),
  venueId: IdSchema.nullable().optional(),
  durationMin: DurationSchema.optional(),
  minAttendance: MinAttendanceSchema.optional(),
  notes: NotesSchema.optional(),
  opponentSquadId: IdSchema.nullable().optional(),
});
export type AdminGatheringUpdateInput = z.infer<typeof AdminGatheringUpdateInputSchema>;

export const AdminGatheringRescheduleInputSchema = z.object({
  gatheringId: IdSchema,
  date: IsoDateSchema,
  time: HHmmSchema,
  /** undefined — оставить площадку, null — снять */
  venueId: IdSchema.nullable().optional(),
  reason: ReasonSchema.default(null),
});
export type AdminGatheringRescheduleInput = z.infer<typeof AdminGatheringRescheduleInputSchema>;

export const AdminGatheringCancelInputSchema = z.object({
  gatheringId: IdSchema,
  reason: z.string().trim().min(2, 'Укажи причину').max(500),
});
export type AdminGatheringCancelInput = z.infer<typeof AdminGatheringCancelInputSchema>;

/**
 * Разовый сбор ops в идущем сезоне отряда (Stage 17, задача 5; ADR-35). Отряд и сезон — из
 * контекста; `capacity` null — вместимость отряда, иначе от `Squad.capacity` до `Venue.capacity`
 * (проверяет сервер); `durationMin` не задан — длительность активности; `tournamentFormat` —
 * только у турнира, null = `STANDARD`.
 */
export const AdminGatheringAddInputSchema = z.object({
  seasonId: IdSchema,
  activityId: IdSchema,
  date: IsoDateSchema,
  time: HHmmSchema,
  venueId: IdSchema.nullable().default(null),
  durationMin: DurationSchema.optional(),
  capacity: z.number().int().min(1).max(64).nullable().default(null),
  notes: NotesSchema.default(null),
  tournamentFormat: TournamentFormatSchema.nullable().default(null),
});
export type AdminGatheringAddInput = z.input<typeof AdminGatheringAddInputSchema>;
export type AdminGatheringAddParsed = z.infer<typeof AdminGatheringAddInputSchema>;

/**
 * Почему «Добавить сбор» сейчас откажет — та же проверка, что в `addGathering` (кнопка неактивна
 * с причиной): флаг `feature.myGatherings` выключен или сезон не идёт.
 */
export const ExtraGatheringBlockSchema = z.enum(['feature_disabled', 'season_not_active']);
export type ExtraGatheringBlock = z.infer<typeof ExtraGatheringBlockSchema>;

// ───────────────────────────── настройки ─────────────────────────────

export const SettingKeySchema = z.enum(
  Object.values(SETTING_KEYS) as [string, ...string[]],
);

export const AdminSettingSetInputSchema = z.object({
  key: SettingKeySchema,
  /** Список — только для вида `cities` (города выкатки флага, ADR-47) */
  value: z.union([z.number(), z.boolean(), z.array(z.string()).max(50)]),
});
export type AdminSettingSetInput = z.infer<typeof AdminSettingSetInputSchema>;

export const SeasonTemplateCodeSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{1,39}$/, 'Код: латиница, цифры и дефис');

export const AdminSeasonTemplateUpsertInputSchema = z.object({
  code: SeasonTemplateCodeSchema,
  name: z.string().trim().min(2, 'Название от 2 символов').max(60),
  slots: SeasonSlotsSchema,
  seasonal: SeasonalSchema.nullable().default(null),
});
export type AdminSeasonTemplateUpsertInput = z.infer<typeof AdminSeasonTemplateUpsertInputSchema>;

export const AdminSeasonTemplateDeleteInputSchema = z.object({ code: SeasonTemplateCodeSchema });

// ───────────────────────────── метрика продлений (Stage 12, задача 7) ─────────────────────────────

/** Повторная покупка сезона: сколько отрядов дожили до продления и сколько людей продлили. */
export const RenewalMetricsSchema = z.object({
  /** Сезонов, по которым уходило предложение продлить */
  offeredSeasons: z.number().int().nonnegative(),
  /** Сезонов-продлений, дошедших до ACTIVE */
  renewedSeasons: z.number().int().nonnegative(),
  /** Доля отрядов, продливших сезон, % */
  squadRenewalRatePct: z.number().int().min(0).max(100),
  /** Участников, получивших предложение */
  offeredMembers: z.number().int().nonnegative(),
  /** Участников, оплативших продление */
  renewedMembers: z.number().int().nonnegative(),
  /** Доля людей, купивших второй сезон, % */
  memberRenewalRatePct: z.number().int().min(0).max(100),
  /** Отряды на паузе после недобора продлений */
  pausedSquads: z.number().int().nonnegative(),
});
export type RenewalMetrics = z.infer<typeof RenewalMetricsSchema>;

export const RenewalMetricsInputSchema = z.object({ cityId: IdSchema.optional() }).default({});
export type RenewalMetricsInput = z.infer<typeof RenewalMetricsInputSchema>;
